import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useGetBalanceQuery } from "../../../store/service/clientBetService";
import { APP_BRAND_MARK, APP_BRAND_TAIL } from "../../../utils/brand";
import "./Client.scss";

const IconMenu = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M4 7h16M4 12h16M4 17h16" strokeLinecap="round" />
  </svg>
);

const IconClose = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
  </svg>
);

const IconHome = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6H10v6H5a1 1 0 0 1-1-1v-9.5z" strokeLinejoin="round" />
  </svg>
);

const IconHistory = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M8 7h8M8 12h8M8 17h5" strokeLinecap="round" />
    <rect x="4" y="3" width="16" height="18" rx="2" />
  </svg>
);

const IconWallet = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <rect x="3" y="6" width="18" height="13" rx="2" />
    <path d="M3 10h18" strokeLinecap="round" />
    <circle cx="16.5" cy="14.5" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

const IconPlus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
    <path d="M12 5v14M5 12h14" strokeLinecap="round" />
  </svg>
);

const IconMinus = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" aria-hidden="true">
    <path d="M5 12h14" strokeLinecap="round" />
  </svg>
);

const IconProfile = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 19.5c1.5-3.5 4-5 7-5s5.5 1.5 7 5" strokeLinecap="round" />
  </svg>
);

const IconDeposit = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M12 4v12M8 12l4 4 4-4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 20h14" strokeLinecap="round" />
  </svg>
);

const IconWithdraw = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M12 20V8M8 12l4-4 4 4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M5 4h14" strokeLinecap="round" />
  </svg>
);

const IconSupport = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M5 12a7 7 0 0 1 14 0v2.5a2.5 2.5 0 0 1-2.5 2.5H15" strokeLinecap="round" />
    <path d="M9 18h3a2 2 0 0 1 2 2v0" strokeLinecap="round" />
  </svg>
);

const IconLogout = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path d="M10 7V5a2 2 0 0 1 2-2h7v18h-7a2 2 0 0 1-2-2v-2" strokeLinecap="round" />
    <path d="M15 12H4m0 0 3-3M4 12l3 3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClientLayout = () => {
  const nav = useNavigate();
  const token = localStorage.getItem("client-token");
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!token) {
      nav("/play/login", { replace: true });
    }
  }, [token, nav]);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const { data: balanceData } = useGetBalanceQuery(undefined, {
    skip: !token,
    pollingInterval: 15000,
  });

  const balance = Number(balanceData?.data?.balance ?? 0);
  const exposure = Number(
    balanceData?.data?.exposure ?? balanceData?.data?.exposer ?? balanceData?.data?.exposureAmount ?? 0
  );
  const username = localStorage.getItem("client-username") || "Player";
  const userId = localStorage.getItem("client-userId") || "";
  const balanceLabel = balance.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const exposureLabel = exposure.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const initials = username
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  const handleLogout = () => {
    localStorage.removeItem("client-token");
    localStorage.removeItem("client-userId");
    localStorage.removeItem("client-username");
    nav("/play/login");
  };

  const go = (path) => {
    setMenuOpen(false);
    nav(path);
  };

  if (!token) {
    return null;
  }

  return (
    <div className="lotus-app">
      <header className="lotus-header lotus-header--dark">
        <div className="lotus-header-row">
          <button type="button" className="lotus-logo" onClick={() => nav("/play")}>
            <span className="lotus-logo-bet">{APP_BRAND_MARK}</span>
            <span className="lotus-logo-in">{APP_BRAND_TAIL}</span>
          </button>

          <div className="lotus-header-wallet">
            <div className="lotus-wallet-bal">
              <strong>
                {userId || username} : {balanceLabel}
              </strong>
            </div>
            <div className="lotus-wallet-exp">
              Exp. <span>{exposureLabel}</span>
            </div>
          </div>
        </div>
      </header>

      <main className="lotus-main">
        <Outlet context={{ balance }} />
      </main>

      <nav className="lotus-bottom-nav">
        <NavLink to="/play" end className={({ isActive }) => (isActive ? "active" : "")}>
          <span className="lotus-nav-icon">
            <IconHome />
          </span>
          <small>Home</small>
        </NavLink>
        <NavLink to="/play/my-bets" className={({ isActive }) => (isActive ? "active" : "")}>
          <span className="lotus-nav-icon">
            <IconHistory />
          </span>
          <small>History</small>
        </NavLink>
        <button
          type="button"
          className="lotus-nav-balance"
          onClick={() => go("/play/profile")}
          title="Balance">
          <span className="lotus-nav-icon">
            <IconWallet />
          </span>
          <small className="lotus-nav-bal-amt">{balanceLabel}</small>
        </button>
        <button
          type="button"
          className={`lotus-nav-menu-btn ${menuOpen ? "active" : ""}`}
          onClick={() => setMenuOpen(true)}
          aria-label="Open menu">
          <span className="lotus-nav-icon">
            <IconMenu />
          </span>
          <small>Menu</small>
        </button>
      </nav>

      <div
        className={`lotus-drawer-backdrop ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
        aria-hidden={!menuOpen}
      />
      <aside className={`lotus-drawer ${menuOpen ? "open" : ""}`} aria-hidden={!menuOpen}>
        <div className="lotus-drawer-head">
          <div className="lotus-drawer-user">
            <div className="lotus-drawer-avatar" aria-hidden="true">
              {initials || <IconProfile />}
            </div>
            <div>
              <strong>{username}</strong>
              <span>{userId}</span>
            </div>
          </div>
          <button type="button" className="lotus-drawer-close" onClick={() => setMenuOpen(false)} aria-label="Close">
            <IconClose />
          </button>
        </div>

        <div className="lotus-drawer-balance">
          <span>Available balance</span>
          <strong>{balanceLabel}</strong>
        </div>

        <nav className="lotus-drawer-nav">
          <button type="button" onClick={() => go("/play")}>
            <span className="lotus-drawer-ico">
              <IconHome />
            </span>
            Home
          </button>
          <button type="button" onClick={() => go("/play/profile")}>
            <span className="lotus-drawer-ico">
              <IconProfile />
            </span>
            Profile & Stats
          </button>
          <button type="button" onClick={() => go("/play/my-bets")}>
            <span className="lotus-drawer-ico">
              <IconHistory />
            </span>
            Bet History
          </button>
          <button type="button" onClick={() => go("/play/deposit")}>
            <span className="lotus-drawer-ico">
              <IconDeposit />
            </span>
            Deposit
          </button>
          <button type="button" onClick={() => go("/play/withdraw")}>
            <span className="lotus-drawer-ico">
              <IconWithdraw />
            </span>
            Withdraw
          </button>
          <a href="https://wa.me/" target="_blank" rel="noreferrer" onClick={() => setMenuOpen(false)}>
            <span className="lotus-drawer-ico">
              <IconSupport />
            </span>
            Support
          </a>
          <button type="button" className="lotus-menu-logout" onClick={handleLogout}>
            <span className="lotus-drawer-ico">
              <IconLogout />
            </span>
            Logout
          </button>
        </nav>
      </aside>
    </div>
  );
};

export default ClientLayout;
